<script setup lang="ts">
const props = defineProps<{ item: GlobalSearchResult; search: string }>()

function _recursiveGet(obj: any, path: string): any {
  const results = []
  const [key, ...parts] = path.split(".")
  const subpath = parts.join(".")

  if (R.isPlainObject(obj)) {
    const value = (obj as any)[key] as any
    if (parts.length === 0) {
      results.push(value)
    } else {
      _recursiveGet(value, subpath).forEach((e: any) => results.push(e))
    }
  } else if (R.isArray(obj)) {
    for (const value of obj) {
      _recursiveGet(value, path).forEach((e: any) => results.push(e))
    }
  }

  return results
}

interface MatchRecord {
  attrname: string
  value: string
  isExactMatch: boolean
}

function toMatchingParts(item: Record<string, any>): MatchRecord[] {
  const results: MatchRecord[] = []
  const iterable: Iterable<[string, { length: number }[]]> = Object.entries(item._matchesPosition)

  for (const [aggkey, matchRecords] of iterable) {
    if (matchRecords.length === 0) continue

    const values = _recursiveGet(item, aggkey).filter((v: any) => R.isString(v)) as string[]

    let isExactMatch = false
    if (props.search.includes(" ")) {
      isExactMatch = values.some((v) => props.search.toLowerCase().includes(v.toLowerCase()))
    } else {
      isExactMatch = values.some((v) => props.search.toLowerCase() === v.toLowerCase())
    }

    results.push({
      attrname: aggkey,
      isExactMatch,
      value: values.join(", "),
    })
  }

  return results
}
const allMatchings = computed(() => toMatchingParts(props.item))

const maxMatchings = 1

const matchings = computed(() =>
  allMatchings.value
    .toSorted((a, b) => {
      if (a.isExactMatch === b.isExactMatch) {
        return 0
      } else if (a.isExactMatch) {
        return -1
      } else {
        return 1
      }
    })
    .slice(0, maxMatchings),
)
const hasMoreMatchings = computed(() => allMatchings.value.length > maxMatchings)

const otherMatchings = computed(() =>
  allMatchings.value.filter(
    (record) => !matchings.value.map((e) => e.attrname).includes(record.attrname),
  ),
)

const otherIsAllExact = computed(() => otherMatchings.value.every((e) => e.isExactMatch))
</script>

<template>
  <div class="flex min-w-0 flex-nowrap items-center justify-end gap-0.5">
    <GlobalSearchMatchBadge
      v-for="record in matchings"
      :key="record.attrname"
      class="truncate"
      v-bind="record"
    />

    <tippy theme="rnx-light" placement="right" class="shrink-0 pb-0.5">
      <UBadge
        v-if="hasMoreMatchings"
        variant="outline"
        :color="otherIsAllExact ? 'rnxblue' : 'gray'"
        :class="otherIsAllExact ? 'opacity-50' : 'opacity-30'"
        class="truncate"
        size="xs"
        :ui="{ rounded: 'rounded-full' }"
      >
        <span class="truncate">+{{ allMatchings.length - maxMatchings }}</span>
      </UBadge>
      <template #content>
        <div class="flex flex-auto items-center gap-1">
          <GlobalSearchMatchBadge
            v-for="record in otherMatchings"
            :key="record.attrname"
            v-bind="record"
          />
        </div>
      </template>
    </tippy>
  </div>
</template>
